// Laboratorio.tsx — modalità di prova: tutti i filtri attivi, uno dopo l'altro, sulla propria fotocamera.
// Avanti e indietro, giro automatico, e un elenco dei filtri che si fermano con un errore o che il tester segna
// come difettosi (da copiare e mandare). Testi dello spazio "admin" (strumenti interni, italiano e inglese).
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFiltri } from "../gioco/usaFiltri.ts";
import { apriFotocamera } from "../gioco/video.ts";
import { preparaRilevamento } from "../gioco/rilevamento.ts";
import { filtriDisponibili, type FiltroDisponibile } from "../servizi/filtri.ts";
import { testiFiltro } from "../filtri/registro.ts";
import { ConsensoVolto } from "../gioco/ConsensoVolto.tsx";
import { voltoAccettato } from "../servizi/account.ts";

const GIRI_SECONDI = [3, 6, 12] as const;

export function Laboratorio({ onChiudi }: { onChiudi: () => void }) {
  // v022: anche i tester vedono la spiegazione dell'analisi del volto prima di accendere la fotocamera
  const [volto, setVolto] = useState(voltoAccettato);
  if (!volto) return <ConsensoVolto onAccettato={() => setVolto(true)} onEsci={onChiudi} />;
  return <LaboratorioAperto onChiudi={onChiudi} />;
}

function LaboratorioAperto({ onChiudi }: { onChiudi: () => void }) {
  const { t, i18n } = useTranslation("admin");
  const [flusso, setFlusso] = useState<MediaStream | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  useEffect(() => {
    let f: MediaStream | null = null;
    let chiuso = false;
    void preparaRilevamento();
    apriFotocamera()
      .then((x) => {
        if (chiuso) x.getTracks().forEach((tr) => tr.stop());
        else setFlusso((f = x));
      })
      .catch((e: unknown) => setErrore(String(e)));
    return () => {
      chiuso = true;
      f?.getTracks().forEach((tr) => tr.stop());
    };
  }, []);
  if (errore) return <p className="admin-errore" role="alert">{t("prova.fotocamera", { errore })}</p>;
  if (!flusso) return <p aria-busy="true">{t("generale.caricamento", { ns: "app" })}</p>;
  return <Banco flusso={flusso} lingua={i18n.language} onChiudi={onChiudi} />;
}

function Banco({ flusso, lingua, onChiudi }: { flusso: MediaStream; lingua: string; onChiudi: () => void }) {
  const { t } = useTranslation("admin");
  const filtri = useFiltri(flusso);
  const [catalogo, setCatalogo] = useState<FiltroDisponibile[]>([]);
  const [indice, setIndice] = useState(0);
  const [giro, setGiro] = useState<number | null>(null);
  const [nome, setNome] = useState("");
  const [difetti, setDifetti] = useState<string[]>([]);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    void filtriDisponibili().then((c) => setCatalogo([...c].sort((a, b) => a.numero - b.numero)));
  }, []);
  const attuale = catalogo[indice] ?? null;
  const vai = (d: number) => setIndice((i) => (catalogo.length ? (i + d + catalogo.length) % catalogo.length : 0));

  // Il filtro si applica appena il motore è pronto, e a ogni cambio.
  const { motore, cambiaPerGioco, errore, attivo } = filtri;
  useEffect(() => {
    if (motore && attuale) cambiaPerGioco(attuale.id);
  }, [motore, attuale, cambiaPerGioco]);
  useEffect(() => {
    if (video.current) video.current.srcObject = motore && filtri.uscitaAttiva ? motore.uscita : flusso;
  }, [motore, filtri.uscitaAttiva, flusso]);
  useEffect(() => {
    if (attuale) void testiFiltro(attuale.id, lingua).then((x) => setNome(x.nome));
  }, [attuale, lingua]);
  // Un filtro che si ferma con un errore finisce nell'elenco; il giro automatico va avanti.
  useEffect(() => {
    if (errore && attuale) setDifetti((d) => (d.includes(`${attuale.numero} ${attuale.id} (errore)`) ? d : [...d, `${attuale.numero} ${attuale.id} (errore)`]));
  }, [errore, attuale]);
  useEffect(() => {
    if (giro === null) return;
    const id = setInterval(() => vai(1), giro * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giro, catalogo.length]);

  const rapporto = useMemo(() => difetti.join("\n"), [difetti]);
  return (
    <div className="laboratorio">
      <video ref={video} autoPlay playsInline muted className="video video-specchio laboratorio-video" />
      <p className="laboratorio-titolo" aria-live="polite">
        {attuale ? `${attuale.numero} · ${nome} · ${attuale.piano}${attuale.prezzoCrediti ? ` ${attuale.prezzoCrediti}` : ""}` : "-"}
        {" "}
        <span className="nota">
          ({indice + 1}/{catalogo.length}) {filtri.stato !== "pronto" ? t("prova.motore", { stato: filtri.stato }) : attivo === attuale?.id ? "✓" : "…"}
        </span>
      </p>
      <div className="azioni-riga">
        <button type="button" className="pulsante pulsante-secondario" onClick={() => vai(-1)}>
          {t("prova.precedente")}
        </button>
        <button type="button" className="pulsante pulsante-primario" onClick={() => vai(1)}>
          {t("prova.successivo")}
        </button>
      </div>
      <div className="azioni-riga">
        <span className="nota">{t("prova.giro")}</span>
        {GIRI_SECONDI.map((s) => (
          <button key={s} type="button" className="chip" aria-selected={giro === s} onClick={() => setGiro(giro === s ? null : s)}>
            {s} s
          </button>
        ))}
        <button
          type="button"
          className="pulsante pulsante-piccolo"
          disabled={!attuale}
          onClick={() => attuale && setDifetti((d) => [...d, `${attuale.numero} ${attuale.id}`])}
        >
          {t("prova.segna_difetto")}
        </button>
      </div>
      {difetti.length > 0 && (
        <>
          <textarea className="campo" readOnly rows={Math.min(8, difetti.length + 1)} value={rapporto} aria-label={t("prova.difetti")} />
          <button type="button" className="pulsante pulsante-piccolo" onClick={() => void navigator.clipboard?.writeText(rapporto).catch(() => undefined)}>
            {t("prova.copia")}
          </button>
        </>
      )}
      <button type="button" className="pulsante pulsante-secondario" onClick={onChiudi}>
        {t("prova.chiudi_laboratorio")}
      </button>
    </div>
  );
}
